import { Fragment, ReactNode } from "react";
import cx from "classnames";
import { flexRender, Row } from "@tanstack/react-table";

import { Flex } from "../../../../../components";

import { getCommonPinningClasses } from "../../internal/getCommonPinningClasses";
import { TableRow } from "../../types";

import { DataTableBodyCell } from "./cells/DataTableBodyCell";

import styles from "./DataTableBody.module.scss";
import { useDTHoverStateContext } from "./context/hover/useDTHoverStateContext";
import { useColumnOrder } from "./useColumnOrder";
import { useDTFocusDispatchContext } from "./context/focus/useDTFocusDispatchContext";

export type DataTableBodyRowProps<T> = {
  columnOrder: ReturnType<typeof useColumnOrder>["columnOrder"];
  row: Row<T>;
  rowIndex: number;
  tableHasSubRows: boolean;
};

export function DataTableBodyRow<T>({
  columnOrder,
  row,
  rowIndex,
  tableHasSubRows,
}: DataTableBodyRowProps<T>) {
  const visibleCells = row.getVisibleCells();

  const canExpand = row.getCanExpand();
  const isExpanded = row.getIsExpanded();
  const depth = row.depth;
  const id = row.id;
  const original = row.original;
  const parentRowCanExpand = row.getParentRow()?.getCanExpand() ?? false;

  const { hoveredColumnId } = useDTHoverStateContext();
  const { focusSubComponent, blurSubComponent } = useDTFocusDispatchContext();

  return (
    <Fragment key={row.id}>
      <div
        role="row"
        aria-rowindex={rowIndex + 1} // aria-rowindex is 1-based, uses sorted position
        aria-expanded={tableHasSubRows && canExpand ? isExpanded : undefined}
        aria-level={
          tableHasSubRows && (canExpand || parentRowCanExpand)
            ? depth + 1
            : undefined
        }
        className={styles["data-table-body-row"]}
      >
        {visibleCells.map((cell) => {
          const isLastInParentGroup = cell.column.parent
            ? cell.column.parent.getLeafColumns().slice(-1)[0] === cell.column
            : false;

          const cellClasses = cx(getCommonPinningClasses(cell.column), {
            [styles["data-table-body-cell-group"]]:
              "columns" in cell.column.columnDef ||
              (cell.column.parent && isLastInParentGroup),
          });
          const columnIndex = columnOrder.indexOf(cell.column.getIndex());
          return (
            <DataTableBodyCell<T>
              key={cell.id}
              cell={cell}
              cellPosition={{ rowIndex, columnIndex }}
              aria-colindex={columnIndex + 1} // aria-colindex is 1-based
              className={cellClasses}
              isHovered={hoveredColumnId === cell.column.id}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </DataTableBodyCell>
          );
        })}
      </div>
      {isExpanded && "subComponent" in (original as TableRow<T>) && (
        <div
          id={`${id}-sub-component`}
          aria-hidden={!isExpanded}
          className={styles["data-table-body-sub-component-row"]}
          onFocus={() => {
            focusSubComponent();
          }}
          onBlur={() => {
            blurSubComponent();
          }}
        >
          <div className={styles["data-table-body-sub-component-container"]}>
            <Flex className={styles["data-table-body-sub-component-content"]}>
              {(original as TableRow<T>).subComponent as ReactNode}
            </Flex>
          </div>
        </div>
      )}
    </Fragment>
  );
}
